import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocale } from 'next-intl'
import {
  cartAllProductsSelector,
  cartAppliedCouponsSelector,
  cartIdSelector,
  cartPricesSelector,
  checkoutSelectedShippingMethodSelector,
  checkoutShippingAddressesSelector,
  resolveCatchMessage,
  setAvailablePaymentMethods,
  setSelectedShippingMethod,
  setShippingAddresses,
  setShippingReady,
  TCatchMessage,
  updateCartTotalsInformation,
  useCart,
  useCountries,
  useLoadingContext,
  userAddressAllSelector,
  userIsLoggedInSelector,
  useToastContext,
} from '@segway/core'
import useCountryTransform from '@segway/core/src/businessHooks/useCountryTransform'
import useUserAddress from '@segway/core/src/businessHooks/useUserAddress'
import {
  CartAddressInput,
  CountryCodeEnum,
  InputMaybe,
  ShippingAddressInput,
} from '@segway/core/src/graphql/generated/graphql'
import {
  useSetShippingAddressesOnCartMutation,
  useSetShippingMethodsOnCartMutation,
} from '@segway/core/src/services/client/checkoutApi'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'
import { useDebounceEffect } from 'ahooks'

import { generateGTMCartItemsParams } from '@/utils'
import reportGTMEvent from '@/utils/gtm'

/**
 * 结算页Shipping地址 Hooks
 */
const useCheckoutShipping = () => {
  const dispatch = useAppDispatch()
  const loading = useLoadingContext()
  const locale = useLocale()
  const { fetchCustomerAddress, createCustomerAddress, updateCustomerAddress } = useUserAddress()
  const { totalsInformation } = useCart()

  const cartId = useAppSelector(cartIdSelector)
  const shippingAddresses = useAppSelector(checkoutShippingAddressesSelector)
  const isLoggedIn = useAppSelector(userIsLoggedInSelector)
  const userAddressAll = useAppSelector(userAddressAllSelector)
  const allProducts = useAppSelector(cartAllProductsSelector)
  const cartPrices = useAppSelector(cartPricesSelector)
  const appliedCoupons = useAppSelector(cartAppliedCouponsSelector)
  const selectedShippingMethod = useAppSelector(checkoutSelectedShippingMethodSelector)
  const { getCountryNameById } = useCountryTransform()
  const toast = useToastContext()
  useCountries() // TODO: 优化(游客时,会与首次加载表单组件查询国家列表重复)

  const [setShippingAddressesOnCartMutation] = useSetShippingAddressesOnCartMutation()
  const [setShippingMethodsOnCartMutation] = useSetShippingMethodsOnCartMutation()

  const [isInit, setIsInit] = useState<boolean>(false)
  const [formVisible, setFormVisible] = useState<boolean>(false)
  const [shippingId, setShippingId] = useState<number | null>(null)
  const [formValues, setFormValues] = useState<CartAddressInput | null | undefined>(null)
  const [isUseAddressListCollapseed, setIsUseAddressListCollapseed] = useState<boolean>(true)
  const [customerAddressEditor, setCustomerAddressEditor] = useState<{
    id: string | number | null
    visible: boolean
  }>({ id: null, visible: false })

  const shippingAddress = useMemo(() => {
    return shippingAddresses?.[0] ?? null
  }, [shippingAddresses])

  const addressListVisible = useMemo(() => {
    return userAddressAll.length > 0 && isLoggedIn
  }, [isLoggedIn, userAddressAll.length])

  const viewMoreBtnVisible = useMemo(() => {
    return userAddressAll.length > 2
  }, [userAddressAll])

  const formCancelBtnVisible = useMemo(() => {
    return addressListVisible || customerAddressEditor.visible
  }, [addressListVisible, customerAddressEditor])

  const addressTitle = useMemo(() => {
    if (shippingAddress) {
      const { firstname, lastname, telephone } = shippingAddress
      return `${firstname} ${lastname} ${telephone}`
    }
    return ''
  }, [shippingAddress])

  const addressDescription = useMemo(() => {
    if (shippingAddress) {
      const { street, city, country, postcode, company, region } = shippingAddress
      return `${company ? `${company}, ` : ''}${street[0]} ${street?.[1] || ''} ${street?.[2] || ''}, ${region?.label ?? ''}, ${city}, ${getCountryNameById(country?.code ?? '')}, ${postcode}`
    }
    return ''
  }, [shippingAddress, getCountryNameById])

  const customerAddressList = useMemo(() => {
    if (userAddressAll.length > 0) {
      const allList = userAddressAll.map((address) => {
        const {
          firstname,
          lastname,
          telephone,
          street,
          city,
          country_code,
          postcode,
          company,
          region,
        } = address
        const street1 = street?.[0] ?? ''
        const street2 = street?.[1] ?? ''
        const street3 = street?.[2] ?? ''
        return {
          address: {
            ...address,
            region: region?.region_code || '',
            region_id: region?.region_id,
            company: company || '',
            street: [street1 || '', street2 || '', street3 || ''] as [string, string, string],
          },
          title: `${firstname} ${lastname} ${telephone}`,
          desc: `${company ? `${company}, ` : ''}${street1}${street2 ? ` ${street2}` : ''}${street3 ? ` ${street3}` : ''}, ${region.region}, ${city}, ${getCountryNameById(country_code)}, ${postcode}`,
        }
      })
      if (isUseAddressListCollapseed) {
        // 列表收起时，显示2个
        // 如果被选中的地址不在第1、2个，将其挪到第一个展示
        const nowAddressIndex = allList.findIndex((item) => {
          return item.address.id === shippingId
        })
        if (nowAddressIndex > 1) {
          return [allList[nowAddressIndex], allList[0]]
        }
        return allList.slice(0, 2)
      }
      return allList
    }
    return []
  }, [getCountryNameById, isUseAddressListCollapseed, userAddressAll, shippingId])

  const isMx = useMemo(() => locale.toLowerCase().includes('mx'), [locale])

  /**
   *  展开/收起用户地址列表
   */
  const triggerViewMore = useCallback(() => {
    setIsUseAddressListCollapseed(!isUseAddressListCollapseed)
  }, [isUseAddressListCollapseed])

  /**
   *  影藏空表单
   */
  const closeForm = useCallback(() => {
    setFormVisible(false)
  }, [])

  /**
   *  编辑地址 游客
   */
  const handleEditAddress = useCallback(() => {
    if (shippingAddress) {
      setFormVisible(true)
      dispatch(setShippingReady(false))
      setFormValues({
        firstname: shippingAddress?.firstname ?? '',
        lastname: shippingAddress?.lastname ?? '',
        company: shippingAddress?.company ?? '',
        street: [
          shippingAddress?.street?.[0] ?? '',
          shippingAddress?.street?.[1] ?? '',
          shippingAddress?.street?.[2] ?? '',
        ],
        city: shippingAddress?.city ?? '',
        region: shippingAddress?.region?.code ?? '',
        region_id: shippingAddress?.region?.region_id,
        postcode: shippingAddress?.postcode ?? '',
        country_code: shippingAddress.country.code,
        telephone: shippingAddress?.telephone ?? '',
        save_in_address_book: true,
      })
    }
  }, [dispatch, shippingAddress])

  /**
   *  登录用户：显示新增地址表单(空表单)
   */
  const handleNewAddress = useCallback(() => {
    setCustomerAddressEditor({
      id: 'new',
      visible: true,
    })
    dispatch(setShippingReady(false))
  }, [dispatch])

  /**
   *  登录用户：取消新增地址表单(空表单)
   */
  const cancelNewAddress = useCallback(() => {
    setCustomerAddressEditor({
      id: null,
      visible: false,
    })
    dispatch(setShippingReady(true))
  }, [dispatch])

  /**
   *  设置Shipping地址
   */
  const onSubmit = useCallback(
    async (values: ShippingAddressInput) => {
      try {
        loading.show()
        const res = await setShippingAddressesOnCartMutation({
          input: {
            cart_id: cartId,
            shipping_addresses: [values],
          },
        })

        if (res?.error) {
          toast.show({
            icon: 'fail',
            content: resolveCatchMessage(res.error as TCatchMessage),
          })
          loading.hide()
          return
        }

        const shippingAddressRes =
          res?.data?.setShippingAddressesOnCart?.cart?.shipping_addresses?.[0]
        const allMethods = shippingAddressRes?.available_shipping_methods ?? []
        const availableMethod = allMethods.find((method) => {
          return method?.available && method?.carrier_code && method?.method_code
        })
        if (shippingAddressRes && availableMethod?.carrier_code && availableMethod?.method_code) {
          dispatch(setSelectedShippingMethod(availableMethod))
          // 更新购物车结算信息
          const totalsInformationRes = await totalsInformation({
            address: {
              city: shippingAddressRes.city,
              company: shippingAddressRes.company,
              country_id: shippingAddressRes.country.code,
              // email
              firstname: shippingAddressRes.firstname,
              lastname: shippingAddressRes.lastname,
              postcode: shippingAddressRes.postcode,
              region: shippingAddressRes?.region?.code ?? '',
              region_id: shippingAddressRes?.region?.region_id,
              // save_in_address_book
              street: shippingAddressRes.street,
              telephone: shippingAddressRes.telephone,
            },
            shipping_carrier_code: availableMethod.carrier_code,
            shipping_method_code: availableMethod.method_code,
          })
          if (totalsInformationRes) {
            dispatch(updateCartTotalsInformation(totalsInformationRes))
          } else {
            toast.show({
              icon: 'fail',
              content: 'Totals information error',
            })
            loading.hide()
            return Promise.resolve()
          }

          // 设置 Shipping Method
          const result = await setShippingMethodsOnCartMutation({
            input: {
              cart_id: cartId,
              shipping_methods: [
                {
                  carrier_code: availableMethod.carrier_code,
                  method_code: availableMethod.method_code,
                },
              ],
            },
          })
          if (result?.error) {
            toast.show({
              icon: 'fail',
              content: resolveCatchMessage(result.error as TCatchMessage),
            })
            loading.hide()
            return
          }
          if (
            result?.data?.setShippingMethodsOnCart?.cart?.shipping_addresses &&
            result?.data?.setShippingMethodsOnCart?.cart?.available_payment_methods
          ) {
            // 保存Shipping地址
            dispatch(
              setShippingAddresses(result.data.setShippingMethodsOnCart.cart.shipping_addresses),
            )
            // 保存支付方式列表
            dispatch(
              setAvailablePaymentMethods(
                result.data.setShippingMethodsOnCart.cart.available_payment_methods,
              ),
            )
            dispatch(setShippingReady(true))
            setFormVisible(false)
          } else {
            toast.show({
              icon: 'fail',
              content: 'Set shipping method error',
            })
            loading.hide()
            return
          }
        } else {
          toast.show({
            icon: 'fail',
            content: 'There is no shipping method',
          })
          loading.hide()
          return
        }
        return Promise.resolve()
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
        return Promise.reject(e)
      } finally {
        loading.hide()
      }
    },
    [
      cartId,
      dispatch,
      loading,
      setShippingAddressesOnCartMutation,
      setShippingMethodsOnCartMutation,
      toast,
      totalsInformation,
    ],
  )

  const setCustomerDefaultShipping = useCallback(() => {
    // 设置默认Shipping
    if (userAddressAll.length > 0) {
      const defaultShipping = userAddressAll.find((item) => item.default_shipping)
      // 设置shipping id后，设置shipping地址方法(setCustomerShippingById)将自动触发调用
      setShippingId(defaultShipping?.id || userAddressAll[0].id || null)
    } else {
      // 显示空表单
      setFormVisible(true)
    }
  }, [userAddressAll])

  /**
   *  提交保存表单中的地址
   */
  const saveFormAddress = useCallback(
    async ({
      values,
      isCustomerEditing = false,
    }: {
      values: CartAddressInput & { default?: boolean }
      isCustomerEditing?: boolean
    }) => {
      try {
        loading.show()
        if (isLoggedIn) {
          // 创建用户地址，得到id
          const params = { ...values }
          const { country_code, region, region_id } = params
          const default_shipping = params?.default ?? false
          const default_billing = params?.default ?? false
          delete params.save_in_address_book
          delete params.region_id
          delete params.default
          const query = {
            ...params,
            country_code: country_code as InputMaybe<CountryCodeEnum>,
            region: {
              region,
              region_id,
            },
            default_shipping,
            default_billing,
          }
          if (isCustomerEditing) {
            // Update address by id
            const address = await updateCustomerAddress({
              id: customerAddressEditor.id as number,
              input: query,
            })
            if (address?.id) {
              // Get customer address list and save address list to state
              await fetchCustomerAddress()
              // 如果更新的当先的shipping地址，重新设置shipping地址
              if (address.id === shippingId) {
                await onSubmit({ customer_address_id: address.id })
              }
              // Close editing form
              setCustomerAddressEditor({
                id: null,
                visible: false,
              })
            }
            loading.hide()
          } else {
            // Create new customer address
            const address = await createCustomerAddress({
              input: query,
            })
            if (address?.id) {
              // Get customer address list and save it to state
              await fetchCustomerAddress()
              // Set shipping id
              setShippingId(address.id)
              // Close addredd form
              setCustomerAddressEditor({
                id: null,
                visible: false,
              })
            }
          }
        } else {
          // 游客
          onSubmit({
            address: values,
          })
        }
      } catch (e) {
        loading.hide()
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      }
    },
    [
      onSubmit,
      loading,
      isLoggedIn,
      updateCustomerAddress,
      customerAddressEditor.id,
      fetchCustomerAddress,
      createCustomerAddress,
      toast,
      shippingId,
    ],
  )

  const handleEditCustomerAddress = useCallback(
    (id: number) => {
      setCustomerAddressEditor({
        id,
        visible: true,
      })
      dispatch(setShippingReady(false))
    },
    [dispatch],
  )

  /**
   *  关闭表单编辑
   */
  const closeCustomerAddressEditor = useCallback(() => {
    setCustomerAddressEditor({
      id: null,
      visible: false,
    })
    dispatch(setShippingReady(true))
  }, [dispatch])

  /**
   *  选择用户地址为shipping地址
   */
  const handleSelectAddressItem = useCallback(
    (id: number) => {
      setShippingId(id)
      // 关闭表单编辑
      closeCustomerAddressEditor()
    },
    [closeCustomerAddressEditor],
  )

  /**
   *  通过shipping id设置shipping地址
   */
  const setCustomerShippingById = useCallback(() => {
    if (shippingId) {
      onSubmit({ customer_address_id: shippingId })
    }
  }, [shippingId, onSubmit])

  /**
   *  设置shipping地址方法监听，参数变动后自动调用（shipping id）
   */
  useEffect(() => {
    setCustomerShippingById()
  }, [setCustomerShippingById])

  useEffect(() => {
    if (isInit) {
      return
    }
    setIsInit(true)
    if (isLoggedIn) {
      // 登录用户
      setCustomerDefaultShipping()
    } else {
      // 游客
      if (shippingAddress) {
        setFormVisible(false)
        onSubmit({
          address: {
            city: shippingAddress.city,
            company: shippingAddress.company,
            country_code: shippingAddress.country.code,
            firstname: shippingAddress.firstname,
            lastname: shippingAddress.lastname,
            postcode: shippingAddress.postcode,
            region: shippingAddress?.region?.code ?? '',
            region_id: shippingAddress?.region?.region_id,
            save_in_address_book: false,
            street: [
              shippingAddress?.street?.[0] ?? '',
              shippingAddress?.street?.[1] ?? '',
              shippingAddress?.street?.[2] ?? '',
            ],
            telephone: shippingAddress.telephone,
          },
        })
      } else {
        // 没有地址，显示空表单
        setFormVisible(true)
      }
    }
  }, [isInit, isLoggedIn, onSubmit, setCustomerDefaultShipping, shippingAddress])

  useDebounceEffect(
    () => {
      if (shippingAddresses && selectedShippingMethod) {
        /**
         * GTM: add_shipping_info
         */
        reportGTMEvent({
          event: 'add_shipping_info',
          ecommerce: {
            currency: cartPrices?.grand_total?.currency,
            value: cartPrices?.subtotal_excluding_tax?.value,
            shipping_tier: selectedShippingMethod.method_title,
            coupon: appliedCoupons && appliedCoupons?.length ? appliedCoupons[0]?.code : '',
            items: generateGTMCartItemsParams(allProducts),
          },
        })
      }
    },
    [shippingAddresses, selectedShippingMethod],
    { wait: 10000 },
  )

  return {
    shippingAddress,
    addressTitle,
    addressDescription,
    formVisible,
    handleNewAddress,
    cancelNewAddress,
    handleEditAddress,
    formValues,
    saveFormAddress,
    addressListVisible,
    viewMoreBtnVisible,
    shippingId,
    handleEditCustomerAddress,
    customerAddressEditor,
    handleSelectAddressItem,
    closeCustomerAddressEditor,
    closeForm,
    formCancelBtnVisible,
    customerAddressList,
    isLoggedIn,
    isUseAddressListCollapseed,
    triggerViewMore,
    isMx,
  }
}

export default useCheckoutShipping
